import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useApolloClient } from '@apollo/client';
import { useJoinEventAsPersonWithPasswordMutation } from '@graphql/mutations/joinEvent.graphql.generated';
import { useEffect } from 'react';
import { withWindow } from '@shared/utils/withWindow';
import { buildAuthUrl, buildReturnPath } from '@shared/utils/navigation';
import { useToast } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { hasGraphQLErrorMessage, NO_VALID_PASSWORD_PROVIDED } from '@shared/utils/checkGraphqlError';
import { addSuppressedError } from '@shared/utils/logger';

interface UnlockFields
  extends Readonly<{
    password: string;
  }> {}

interface UseUnlockControllerProps {
  eventId: string;
  onUnlockSuccess: () => void | Promise<void>;
}

export function useUnlockController({ eventId, onUnlockSuccess }: UseUnlockControllerProps) {
  const { toast } = useToast();
  const [joinEvent] = useJoinEventAsPersonWithPasswordMutation();
  const { tq } = useTranslation('guestUnlock');
  const client = useApolloClient();

  const formik = useFormik<UnlockFields>({
    initialValues: {
      password: '',
    },
    validationSchema: Yup.object<UnlockFields>({
      password: Yup.string()
        .trim()
        .required(tq`Password’s required`),
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async values => {
      try {
        const responseJoinEvent = await joinEvent({
          variables: {
            eventId,
            payload: { eventPassword: values.password.trim() },
          },
        });
        if (responseJoinEvent?.data?.joinEventAsPersonWithPassword?.hasUnlocked) {
          // joinEvent mints a *new* EventSession id (not a mutation of the anonymous one), so
          // cache normalization alone leaves the cached `eventByName.activeSession` pointing at
          // the stale anonymous session. Evict that field + gc so its observers re-resolve
          // post-unlock — notably `useGuestSiteUnlockState`, which is `skip`-guarded; a
          // `refetchQueries` force-run would bypass that guard (see apollo cache convention).
          // Both GuestSiteUnlockState and GetEventCTAButtonsData read the *same*
          // `Event.activeSession`, so evicting it invalidates both: the (always-mounted) CTA
          // observer re-fetches and flips `hasUnlocked`, driving the dialog's in-place
          // UnlockForm -> FindInviteForm transition. The full-page gate needs a *synchronous*
          // fresh read at success time, so it fetches the CTA session itself in onUnlockSuccess
          // (it has the event handle; this router-independent controller does not).
          const eventCacheId = client.cache.identify({ __typename: 'Event', id: eventId });
          if (eventCacheId) {
            client.cache.evict({ id: eventCacheId, fieldName: 'activeSession' });
            client.cache.gc();
          }
          // Await so `isSubmitting` stays true through the gate's async post-unlock fetch and
          // no promise floats unhandled. onUnlockSuccess owns its own failures (the gate falls
          // back to a reload), so it never rejects into the wrong-password catch below.
          await onUnlockSuccess();
        }
      } catch (error) {
        if (hasGraphQLErrorMessage(error, NO_VALID_PASSWORD_PROVIDED)) {
          addSuppressedError(error, { context: 'UnlockForm', operation: 'onSubmit', eventId });
        }
        formik.setFieldError('password', 'incorrect');
      }
    },
  });

  const onSignInHandler = () => {
    withWindow(global => {
      global.location.href = buildAuthUrl('/login', buildReturnPath(global.location));
    });
  };

  const inputError = formik.errors.password;

  useEffect(() => {
    if (inputError === 'incorrect') {
      toast(tq`Wrong password. Double-check what you’ve typed, or try copying and pasting.`);
    }

    if (inputError && inputError !== 'incorrect') {
      toast(inputError);
    }
  }, [inputError, toast, tq]);

  return {
    formik,
    loading: formik.isSubmitting,

    inputError: formik.errors.password,
    onSignInHandler,
  };
}
